import { CSSProperties, memo } from 'react'
import { Field } from 'react-final-form'
import { Flex, Radio, Typography } from '@jisr-hr/ds'
import { IdTypes } from 'types/sharedResponse'

export type OptionsType = {
  id: IdTypes
  name_i18n: string
  testId?: string
  disabled?: boolean
  helperText?: string
}

export type NewRadioFieldPropsType = {
  labelKey?: string
  name: string
  onChange?: (
    val: React.ChangeEvent<HTMLInputElement>,
    option: Record<string, string | IdTypes | boolean>,
  ) => void
  validate?: (value: unknown) => void
  options: OptionsType[]
  valueKey?: string
  value?: IdTypes
  dir?: 'vertical' | 'horizontal'
  gap?: number
  className?: string
  flexStyle?: CSSProperties
  disabled?: boolean
  optionsClassName?: string
}

const NewRadioField = ({
  labelKey = 'label',
  name,
  onChange,
  options,
  valueKey = 'value',
  value,
  validate,
  dir = 'horizontal',
  gap,
  className,
  flexStyle,
  disabled,
  optionsClassName,
}: NewRadioFieldPropsType): JSX.Element => {
  const isHorizontal = dir === 'horizontal'

  return (
    <Flex
      flexCol={dir === 'vertical'}
      style={{ ...flexStyle, gap, ...(isHorizontal && { rowGap: '6px', flexWrap: 'wrap' }) }}
      flexWrap={dir === 'vertical'}
      className={className}
    >
      {options.map((option, i) => (
        <Field
          name={name}
          key={i}
          type="radio"
          checked={value === option?.id}
          validate={validate}
        >
          {({ input, meta }): JSX.Element => (
            <>
              <div className={optionsClassName}>
                <Radio
                  {...input}
                  label={option[labelKey]}
                  value={option[valueKey]}
                  onChange={(val: React.ChangeEvent<HTMLInputElement>): void => {
                    input.onChange(val)

                    onChange?.(val, option)
                  }}
                  active={value === option?.id}
                  testId={option.testId}
                  disabled={option.disabled || disabled}
                  description={option.helperText}
                  id={option[valueKey]}
                />
              </div>
              {i === options.length - 1 && meta.error && meta.touched && (
                <Typography
                  text={meta.error}
                  textColor="color/fg/danger/default"
                  variant="body-new/regular"
                  {...(isHorizontal && { style: { flexBasis: '100%' } })}
                />
              )}
            </>
          )}
        </Field>
      ))}
    </Flex>
  )
}

export default memo(NewRadioField)
